import React, { type ReactNode, use, useMemo } from "react";
import { CompanyAccountingYearContext } from "@domain/context/CompanyContext";
import { Model, Utils } from "@binale-tech/shared";
import { Tooltip } from "@heroui/react";
import dayjs from "dayjs";
import { getQuarterPeriods, isQuarterPeriod, type QuarterPeriod } from "@domain/utils/quarterPeriods";
import type { ColDef, ColGroupDef, ICellRendererParams } from "ag-grid-community";
import { FormattedMessage, type IntlShape, useIntl } from "react-intl";
import { getBwaValueColumns } from "../bwaPeriodColumns";

export interface BWATableRecord {
    key: number;
    group?: ReactNode;
    subGroup?: ReactNode;
    konto?: Model.IExtNum;
    isFocused?: boolean;
    /** keyed by Model.Period: 0 = FirstDayOfYear (Saldenvortrag), 1-12 = months, 13 = LastDayOfYear (Abschluss) */
    periodAmounts: Map<number, number>;
}

export type BwaAmountRow = Pick<BWATableRecord, "konto" | "periodAmounts">;

const formatNumber = (tableItem: BwaAmountRow, amount: number, showZeroes?: boolean): string | null => {
    const formatted = Utils.CurrencyUtils.currencyFormat(amount);
    if (showZeroes) {
        return formatted;
    }
    if (tableItem.konto) {
        return amount !== 0 ? formatted : null;
    }
    return formatted;
};

/** sum of the given Model.Period keys (missing keys count as 0), formatted with the grid's zero rule */
export const getterPeriods = (
    tableItem: BwaAmountRow,
    periods: readonly number[],
    showZeroes?: boolean,
): string | null => {
    const amount = periods.reduce((acc, period) => acc + (tableItem.periodAmounts.get(period) ?? 0), 0);
    return formatNumber(tableItem, amount, showZeroes);
};

export const getterPeriod = (tableItem: BwaAmountRow, period: number) => getterPeriods(tableItem, [period]);

export const getterQuart = (tableItem: BwaAmountRow, quarter: QuarterPeriod) =>
    getterPeriods(tableItem, getQuarterPeriods(quarter));

export const getterYear = (tableItem: BWATableRecord) => {
    const amount = Array.from(tableItem.periodAmounts.values()).reduce((acc, cur) => acc + cur, 0);
    return formatNumber(tableItem, amount, true);
};

export const getPeriodHeaderName = (period: number): string => {
    if (isQuarterPeriod(period)) {
        return `Quartal ${period - 40}`;
    }
    if (period === Model.Period.FirstDayOfYear) {
        return "01.01.";
    }
    if (period === Model.Period.LastDayOfYear) {
        return "31.12.";
    }
    return dayjs.months()[period - 1];
};

export const getBwaAmountColumns = (intl: IntlShape, year: number, period: number | null): ColDef<BWATableRecord>[] => {
    if (Number.isFinite(period)) {
        const cumulative = getBwaValueColumns(intl, year, period)[1];
        return [
            {
                colId: `month.${period}`,
                headerName: getPeriodHeaderName(period),
                width: 135,
                cellClass: "text-right",
                cellRenderer: ({ data }: ICellRendererParams<BWATableRecord>) =>
                    isQuarterPeriod(period) ? getterQuart(data, period) : getterPeriod(data, period),
            },
            {
                colId: "sum.ytd",
                headerName: cumulative.header,
                width: 220,
                cellClass: "text-right",
                cellRenderer: ({ data }: ICellRendererParams<BWATableRecord>) =>
                    getterPeriods(data, cumulative.periods),
            },
        ];
    }

    return [
        Model.Period.FirstDayOfYear,
        ...dayjs.months().map((_, index) => index + 1),
        Model.Period.LastDayOfYear,
    ].map((periodKey) => ({
        colId: `month.${periodKey}`,
        headerName: getPeriodHeaderName(periodKey),
        width: 135,
        cellClass: "text-right",
        cellRenderer: ({ data }: ICellRendererParams<BWATableRecord>) => getterPeriod(data, periodKey),
    }));
};

export const useBWAColumns = (period: number | null, year: number) => {
    const { yearConfig } = use(CompanyAccountingYearContext);
    const intl = useIntl();

    return useMemo<(ColDef<BWATableRecord> | ColGroupDef<BWATableRecord>)[]>(() => {
        const defs: (ColDef<BWATableRecord> | ColGroupDef<BWATableRecord>)[] = [
            {
                colId: "konto",
                headerName: "",
                headerComponent: () => <FormattedMessage id="app.fields.konto" />,
                width: 95,
                pinned: "left",
                colSpan: ({ data }) => (data?.konto ? 1 : 2),
                cellRenderer: ({ data }: ICellRendererParams<BWATableRecord>) => {
                    if (data.konto) {
                        return data.konto.getExtNumPrint(yearConfig.kontoExt);
                    }
                    if (data.subGroup) {
                        return <div className="pl-12 whitespace-break-spaces">{data.subGroup}</div>;
                    }
                    if (data.group) {
                        return data.group;
                    }
                    return null;
                },
            },
            {
                colId: "bezeichnung",
                headerName: "",
                headerComponent: () => <FormattedMessage id="app.fields.bezeichnung" />,
                width: 290,
                pinned: "left",
                cellRenderer: ({ data }: ICellRendererParams<BWATableRecord>) =>
                    data.konto ? (
                        <Tooltip delay={0} closeDelay={0}>
                            <Tooltip.Trigger>
                                <div className="truncate w-[270px]">{data.konto.name}</div>
                            </Tooltip.Trigger>
                            <Tooltip.Content placement="right">
                                <p>{data.konto.name}</p>
                            </Tooltip.Content>
                        </Tooltip>
                    ) : null,
            },
        ];

        defs.push(...getBwaAmountColumns(intl, year, period));

        defs.push({
            colId: "sum.j",
            headerName: "",
            headerComponent: () => <FormattedMessage id="app.fields.date.year" />,
            width: 145,
            cellClass: "bg-default text-right",
            cellRenderer: ({ data }: ICellRendererParams<BWATableRecord>) => getterYear(data),
        });

        return defs;
    }, [intl, period, year, yearConfig]);
};
